"use client";

import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";
import { useLanguage } from "@/context/LanguageContext";

export default function HeroSection() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background moroccan-pattern pt-24">
      {/* Floating decorative images */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full overflow-hidden opacity-30 animate-float hidden md:block">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_12532c850-1767719242746.png"
          alt="Traditional Tunisian carpet with geometric patterns in red and gold"
          className="w-full h-full object-cover" />
      </div>

      <div className="absolute bottom-32 right-10 w-40 h-40 rounded-full overflow-hidden opacity-40 animate-float hidden md:block [animation-delay:2s]">
        <AppImage
          src="https://images.unsplash.com/photo-1703289629694-67923c9d241c"
          alt="Handcrafted ceramic pottery with traditional Tunisian blue and white patterns"
          className="w-full h-full object-cover" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Main Heading */}
          <div className="mb-8 reveal will-animate">
            <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-bold leading-none tracking-tight text-foreground mb-4">
              {t("shop_hero_title")}
              <span className="block mt-2 gradient-text">{t("shop_hero_subtitle")}</span>
            </h1>
          </div>

          {/* Subheading */}
          <div className="mb-12 reveal will-animate">
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {t("shop_hero_desc")}
              <span className="font-semibold text-foreground">{t("shop_hero_desc_bold")}</span>
              {t("shop_hero_desc_end")}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center reveal will-animate">
            <a
              href="#products"
              className="group px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary/90 transition-all hover:scale-105 shadow-lg flex items-center gap-2">
              <span>{t("shop_hero_explore")}</span>
              <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/adventure-reservations"
              className="px-8 py-4 bg-accent text-white rounded-full font-semibold text-lg hover:bg-accent/90 transition-all hover:scale-105 shadow-lg">
              {t("shop_hero_book")}
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-8 reveal will-animate">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="CheckBadgeIcon" size={32} className="text-primary" variant="solid" />
              </div>
              <p className="text-sm font-semibold text-foreground">{t("shop_hero_authentic")}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-secondary/10 rounded-full flex items-center justify-center">
                <Icon name="TruckIcon" size={32} className="text-secondary" />
              </div>
              <p className="text-sm font-semibold text-foreground">{t("shop_hero_shipping")}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="HandThumbUpIcon" size={32} className="text-success" variant="solid" />
              </div>
              <p className="text-sm font-semibold text-foreground">{t("shop_hero_customers")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDownIcon" size={32} className="text-muted-foreground" />
      </div>
    </section>
  );
}